import { Box, Grid, Typography, useTheme } from '@mui/material'
import { C100Banner, C100Timeframe } from '../../_types/carnivore100.type'
import { c100Tokens } from './c100-tokens'

const MONO = "'IBM Plex Mono', monospace"

const fmtPct = (n: number) => `${n >= 0 ? '+' : ''}${n.toFixed(2)}%`

export default function C100BannerView({
  banner,
  timeframe
}: {
  banner: C100Banner
  timeframe: C100Timeframe
}) {
  const theme = useTheme()
  const t = c100Tokens(theme)
  const avgColor = banner.avgMovePct >= 0 ? t.gain : t.loss

  const padNum = (n: number) => String(n).padStart(3, '0')

  return (
    <Box
      sx={{
        bgcolor: t.card,
        border: `1px solid ${t.hairline}`,
        borderRadius: 2,
        overflow: 'hidden',
        mb: 2,
        boxShadow: theme.palette.mode === 'light' ? '0 1px 4px rgba(0,0,0,0.07)' : 'none'
      }}
    >
      {/* Striped orange top accent bar */}
      <Box
        sx={{
          height: 4,
          background: `repeating-linear-gradient(45deg, #e25a0b 0 8px, transparent 8px 14px)`
        }}
      />

      <Box sx={{ p: { xs: 2.5, md: 3 } }}>
        {/* Top line */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.12em',
              color: t.text,
              textTransform: 'uppercase'
            }}
          >
            CARNIVORE 100
            <Box
              component="span"
              sx={{ color: t.muted, fontWeight: 400, ml: 1 }}
            >
              · {banner.nameCount} names · {banner.sectorCount} sectors
            </Box>
          </Typography>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: 11,
              letterSpacing: '0.08em',
              color: t.muted
            }}
          >
            CH.{padNum(banner.nameCount)}
          </Typography>
        </Box>

        <Grid container spacing={3} alignItems="flex-start">
          {/* Hero block */}
          <Grid item xs={12} sm={4}>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '0.1em',
                color: t.muted,
                textTransform: 'uppercase',
                mb: 0.5,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              <Box
                component="span"
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: avgColor,
                  display: 'inline-block',
                  flexShrink: 0
                }}
              />
              AVG MOVE · {timeframe}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 44,
                  fontWeight: 600,
                  lineHeight: 1,
                  color: avgColor,
                  fontVariantNumeric: 'tabular-nums'
                }}
              >
                {fmtPct(banner.avgMovePct).replace('%', '')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: MONO,
                  fontSize: 20,
                  fontWeight: 400,
                  color: t.muted,
                  lineHeight: 1,
                  mb: 0.5
                }}
              >
                %
              </Typography>
            </Box>
          </Grid>

          {/* Lead */}
          <Grid item xs={6} sm={4}>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '0.1em',
                color: t.muted,
                textTransform: 'uppercase',
                mb: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              ↗ LEAD · {timeframe}
            </Typography>
            {banner.lead ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 15,
                      fontWeight: 600,
                      color: t.gain,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {banner.lead.ticker}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 13,
                      fontWeight: 500,
                      color: t.gain,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {fmtPct(banner.lead.pct)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: t.muted,
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 180
                  }}
                >
                  {banner.lead.name}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontFamily: MONO, color: t.muted, fontSize: 15 }}>—</Typography>
            )}
          </Grid>

          {/* Laggard */}
          <Grid item xs={6} sm={4}>
            <Typography
              sx={{
                fontFamily: MONO,
                fontSize: 10,
                letterSpacing: '0.1em',
                color: t.muted,
                textTransform: 'uppercase',
                mb: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5
              }}
            >
              ↘ LAGGARD · {timeframe}
            </Typography>
            {banner.laggard ? (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 15,
                      fontWeight: 600,
                      color: t.loss,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {banner.laggard.ticker}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: MONO,
                      fontSize: 13,
                      fontWeight: 500,
                      color: t.loss,
                      fontVariantNumeric: 'tabular-nums'
                    }}
                  >
                    {fmtPct(banner.laggard.pct)}
                  </Typography>
                </Box>
                <Typography
                  sx={{
                    fontSize: 12,
                    color: t.muted,
                    mt: 0.25,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 180
                  }}
                >
                  {banner.laggard.name}
                </Typography>
              </Box>
            ) : (
              <Typography sx={{ fontFamily: MONO, color: t.muted, fontSize: 15 }}>—</Typography>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  )
}
